/**
 * Korint-staff impersonation sessions.
 *
 * `super` mints a short-lived token and opens the target app at
 * `/impersonate#impersonationToken=…`. The token rides in the URL *fragment*, so
 * it never reaches a server or a Referer header; `consumeImpersonationFragment`
 * strips it before anything else runs.
 *
 * Stored in `sessionStorage`, not `localStorage`: the session dies with the tab,
 * and it never bleeds into the staff member's other tabs.
 *
 * Deliberate duplicate of `frontends/apps/tenant-frontend/src/auth/impersonation.ts`
 * — that app has no korint-sdk dependency. Fix both or neither.
 */

const STORAGE_KEY = 'korint.impersonation.token';

/**
 * `sessionStorage` throws outright when storage is blocked (Safari private mode,
 * a managed browser, an exhausted quota) rather than returning null. Every access
 * goes through these so a blocked browser degrades to "no session" instead of
 * throwing out of the caller — the handoff runs during app bootstrap, where an
 * uncaught throw leaves a blank page with the token still in the address bar.
 */
const readStoredToken = (): string | null => {
  try {
    return sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
};

const writeStoredToken = (token: string): boolean => {
  try {
    sessionStorage.setItem(STORAGE_KEY, token);
    return true;
  } catch {
    return false;
  }
};

const removeStoredToken = (): void => {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing was stored if storage is unavailable.
  }
};

export const IMPERSONATION_FRAGMENT_KEY = 'impersonationToken';

export type ImpersonationSession = {
  readonly token: string;
  readonly targetUserId: string;
  readonly actor: string;
  readonly expiresAt: Date;
};

const decodeSegment = (segment: string): unknown => {
  const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
  // `atob` yields latin1 bytes, so a multi-byte claim (an accented actorUsername)
  // would reach the banner as mojibake without decoding them as UTF-8.
  const bytes = Uint8Array.from(atob(padded), character => character.charCodeAt(0));
  return JSON.parse(new TextDecoder().decode(bytes));
};

/**
 * Reads the display claims off the token. Purely for the banner — the gateway is
 * what actually validates the signature, tenant and revocation on every request.
 */
const parseToken = (token: string): ImpersonationSession | null => {
  const segment = token.split('.')[1];
  if (!segment) return null;

  let payload: unknown;
  try {
    payload = decodeSegment(segment);
  } catch {
    return null;
  }

  if (typeof payload !== 'object' || payload === null) return null;
  const { sub, exp, actorSub, actorUsername } = payload as Record<string, unknown>;

  // `Number.isFinite`, not `typeof`: `Infinity` is a number and would produce an
  // Invalid Date, whose `<=` comparisons are all false — so an expired-check would
  // treat the token as live forever.
  if (typeof sub !== 'string' || !Number.isFinite(exp) || typeof actorSub !== 'string') {
    return null;
  }

  const expiresAt = new Date((exp as number) * 1000);
  if (Number.isNaN(expiresAt.getTime())) return null;

  return {
    token,
    targetUserId: sub,
    actor: typeof actorUsername === 'string' && actorUsername ? actorUsername : actorSub,
    expiresAt,
  };
};

export const readImpersonationSession = (now: Date = new Date()): ImpersonationSession | null => {
  const token = readStoredToken();
  if (!token) return null;

  const session = parseToken(token);
  if (!session || session.expiresAt <= now) {
    removeStoredToken();
    return null;
  }

  return session;
};

export const storeImpersonationSession = (token: string): ImpersonationSession | null => {
  const session = parseToken(token);
  if (!session || session.expiresAt <= new Date()) return null;

  if (!writeStoredToken(token)) return null;
  return session;
};

export const clearImpersonationSession = (): void => removeStoredToken();

/**
 * Stamped on the axios request config when the bearer came from an impersonation
 * session. The 401 handler must decide from what the request actually carried:
 * re-reading storage there is too late, because an expired session has already
 * been evicted by then.
 */
export const IMPERSONATED_REQUEST_FLAG = '_korintImpersonatedRequest' as const;

export type ImpersonatedRequestMarker = { [IMPERSONATED_REQUEST_FLAG]?: boolean };

/**
 * Pulls the token out of a location fragment. `#a=b&impersonationToken=…` is
 * parsed as a query string.
 */
export const readTokenFromFragment = (hash: string): string | null => {
  const params = new URLSearchParams(hash.replace(/^#/, ''));
  return params.get(IMPERSONATION_FRAGMENT_KEY);
};
